import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import { sendEmail } from '../emailService'
import { sendWhatsApp } from '../whatsappClient'
import { createMeetingLink } from '../meetHelper'
import { checkTeacherAvailability } from '../availabilityHelper'
import TeacherSlotPicker from './TeacherSlotPicker'

function fmtDate(d) {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', {
    weekday:'short', day:'2-digit', month:'short', year:'numeric'
  })
}
function fmtTime(t) {
  if (!t) return ''
  return new Date('2000-01-01T' + t).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', hour12:true })
}
function parseTimeInput(value) {
  const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i)
  if (!match) return ''
  let hour = Number(match[1])
  const minute = Number(match[2])
  const meridiem = match[3]?.toUpperCase()
  if (minute > 59) return ''
  if (meridiem) {
    if (hour < 1 || hour > 12) return ''
    if (meridiem === 'AM' && hour === 12) hour = 0
    if (meridiem === 'PM' && hour !== 12) hour += 12
  } else if (hour > 23) return ''
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

const STATUS_STYLE = {
  'New':            { bg:'rgba(30,144,255,0.12)',  color:'#5aabff'  },
  'Contacted':      { bg:'rgba(139,92,246,0.12)',  color:'#a78bfa'  },
  'Demo Scheduled': { bg:'rgba(244,163,53,0.12)',  color:'#f4a335'  },
  'Enrolled':       { bg:'rgba(16,185,129,0.12)',  color:'#34d399'  },
  'Lost':           { bg:'rgba(239,68,68,0.12)',   color:'#f87171'  },
}

// creditTo: salesperson to credit the demo to (sets the lead's assigned_to).
// showContact: false hides the lead's phone and email (per-salesperson setting).
export function ScheduleDemoModal({ lead, onClose, onSaved, creditTo = null, showContact = true }) {
  const [teachers,   setTeachers]   = useState([])
  const [teacherId,  setTeacherId]  = useState('')
  const [date,       setDate]       = useState('')
  const [time,       setTime]       = useState('')
  const [timeInput,  setTimeInput]  = useState('')
  const [duration,   setDuration]   = useState(60)
  const [topic,      setTopic]      = useState(lead?.course_interest ? `${lead.course_interest} Demo` : 'Free Demo Class')
  const [notes,      setNotes]      = useState('')
  const [sendMail,   setSendMail]   = useState(true)
  const [busy,       setBusy]       = useState(false)
  const [meetStatus, setMeetStatus] = useState('')
  const [err,        setErr]        = useState('')
  const [availWarning,     setAvailWarning]     = useState('') // outside-hours — needs override click
  const [confirmedOverride, setConfirmedOverride] = useState(false)

  useEffect(() => {
    supabase.from('profiles').select('id,full_name').eq('role','teacher').order('full_name')
      .then(({ data }) => setTeachers(data || []))
  }, [])

  // Any change to who/when resets a prior override — re-check next submit.
  useEffect(() => { setAvailWarning(''); setConfirmedOverride(false) }, [teacherId, date, time, duration])

  async function submit(e, overriding = false) {
    e?.preventDefault()
    if (!date) return setErr('Select a date.')
    const selectedTime = parseTimeInput(timeInput)
    if (!selectedTime) return setErr('Enter a valid time, for example 07:30 PM.')
    setTime(selectedTime)
    setErr('')

    // Availability check — real class conflicts always hard-block; being
    // outside the teacher's declared hours only warns, with one more
    // click to proceed (real one-off exceptions happen).
    if (teacherId && selectedTime && !confirmedOverride && !overriding) {
      const check = await checkTeacherAvailability({ teacherId, date, startTime: selectedTime, durationMinutes: duration })
      if (check.conflicts.length > 0) {
        return setErr(`⛔ ${check.reason}`)
      }
      if (!check.withinAvailability) {
        setAvailWarning(check.reason)
        return
      }
    }
    if (overriding) setConfirmedOverride(true)

    setBusy(true); setMeetStatus('creating')

    const teacher = teachers.find(t => t.id === teacherId)
    const d       = new Date(date + 'T00:00:00')

    // 1. Auto-generate meeting link (Jitsi — instant join, no host needed)
    const result   = await createMeetingLink(topic || `Demo — ${lead.full_name}`, date, selectedTime, duration)
    const meetLink = result?.url || null
    setMeetStatus(meetLink ? 'done' : 'failed')

    // 2. Insert demo class
    const { error } = await supabase.from('classes').insert({
      title:        topic || `Demo — ${lead.full_name}`,
      teacher_id:   teacherId || null,
      teacher_name: teacher?.full_name || null,
      class_date:      date,
      start_time:      selectedTime,
      duration:        `${duration}m`,
      duration_minutes: duration,
      batch:        'demo',
      created_by:   'admin',
      meet_link:    meetLink || null,
    })
    if (error) { setErr(error.message); setBusy(false); return }

    // 3. Update lead status
    await supabase.from('leads').update({
      status:    'Demo Scheduled',
      demo_date: `${date}T${selectedTime}:00`,
      notes:     notes || null,
      ...(creditTo ? { assigned_to: creditTo } : {}),
    }).eq('id', lead.id)

    // 4. Add to calendar
    await supabase.from('events').insert({
      title:        `Demo: ${lead.full_name}`,
      event_type:   'demo',
      day:          d.getDate(),
      month:        d.getMonth() + 1,
      year:         d.getFullYear(),
      time:         selectedTime,
      teacher_name: teacher?.full_name || null,
    })

    // 5. Send demo confirmation email + WhatsApp to lead
    if (sendMail && lead.email) {
      sendEmail('demo_confirmation', lead.email, {
        name:        lead.full_name,
        classTitle:  topic || 'Free Demo Class',
        classDate:   fmtDate(date),
        startTime:   fmtTime(selectedTime),
        teacherName: teacher?.full_name || null,
        zoomLink:    meetLink || null,
        duration:    duration,
      })
    }
    if (lead.phone) {
      // demo_confirmation_wa is a POSITIONAL template — array order must
      // match {{1}}..{{4}} in the template body: name, class_name, date_time, join_link.
      sendWhatsApp('demo_confirmation_wa', lead.phone, [
        lead.full_name,
        topic || 'Free Demo Class',
        `${fmtDate(date)} at ${fmtTime(selectedTime)}`,
        meetLink || 'to be shared',
      ])
    }

    // 6. Send notification to teacher if assigned
    if (teacherId && teacher) {
      const { data: teacherProfile } = await supabase
        .from('profiles').select('email,phone').eq('id', teacherId).single()
      if (teacherProfile?.email) {
        sendEmail('demo_confirmation', teacherProfile.email, {
          name:        teacher.full_name,
          classTitle:  `Demo with ${lead.full_name} — ${topic || 'Free Demo Class'}`,
          classDate:   fmtDate(date),
          startTime:   fmtTime(selectedTime),
          teacherName: teacher.full_name,
          zoomLink:    meetLink || null,
          duration:    duration,
        })
      }
      if (teacherProfile?.phone) {
        sendWhatsApp('demo_confirmation_wa', teacherProfile.phone, [
          teacher.full_name,
          `Demo with ${lead.full_name} — ${topic || 'Free Demo Class'}`,
          `${fmtDate(date)} at ${fmtTime(selectedTime)}`,
          meetLink || 'to be shared',
        ])
      }
    }

    setBusy(false)
    onSaved?.(meetLink)
  }

  const inp = { width:'100%', background:'var(--bg-input)', border:'0.5px solid rgba(var(--fg-rgb),0.12)', borderRadius:'10px', padding:'9px 12px', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', outline:'none', fontFamily:'inherit', colorScheme:'dark', boxSizing:'border-box' }
  const lbl = { display:'block', fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a30))', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'4px', marginTop:'10px' }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
      <div style={{ background:'var(--bg-page)', border:'1px solid rgba(139,92,246,0.3)', borderRadius:'16px', padding:'24px', width:'100%', maxWidth:'440px', boxShadow:'0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
          <div>
            <div style={{ fontSize:'16px', fontWeight:800, color:'var(--fg-solid)' }}>🎯 Schedule Demo</div>
            <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))', marginTop:'2px' }}>for {lead.full_name}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'rgba(var(--fg-rgb),var(--a40))', fontSize:'22px', cursor:'pointer', lineHeight:1 }}>×</button>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'9px 12px', background:'rgba(30,144,255,0.07)', border:'0.5px solid rgba(30,144,255,0.2)', borderRadius:'9px', marginBottom:'4px', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a50))' }}>
          {meetStatus === 'creating' ? (
            <><span style={{ display:'inline-block', width:'12px', height:'12px', border:'2px solid rgba(30,144,255,0.3)', borderTopColor:'#1e90ff', borderRadius:'50%', animation:'spin .7s linear infinite' }}/>Generating meeting link...</>
          ) : meetStatus === 'done' ? (
            <><span style={{ color:'#34d399' }}>✓</span> Meeting link ready — instant join, no host needed</>
          ) : meetStatus === 'failed' ? (
            <><span style={{ color:'#f87171' }}>⚠</span> Link failed — demo saved without it</>
          ) : (
            <><span>🎥</span> Meeting link will be <strong style={{ color:'#5aabff' }}>auto-generated</strong> when you schedule</>
          )}
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', background:'rgba(var(--fg-rgb),0.04)', borderRadius:'10px', marginBottom:'4px' }}>
          <div style={{ width:'32px', height:'32px', borderRadius:'50%', background:'rgba(139,92,246,0.2)', color:'#a78bfa', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'13px', fontWeight:800, flexShrink:0 }}>
            {lead.full_name?.charAt(0).toUpperCase()}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:'13px', fontWeight:600, color:'var(--fg-solid)' }}>{lead.full_name}</div>
            <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))' }}>
              {showContact && lead.phone && `📞 ${lead.phone} · `}
              {showContact && lead.email && `📧 ${lead.email} · `}
              {lead.course_interest && `🎸 ${lead.course_interest}`}
            </div>
          </div>
        </div>

        <form onSubmit={submit}>
          <label style={lbl}>Demo Topic</label>
          <input style={inp} type="text" value={topic} onChange={e=>setTopic(e.target.value)} placeholder="e.g. Guitar Intro Demo" />

          <label style={lbl}>Assign Teacher</label>
          <select style={{ ...inp, colorScheme:'dark' }} value={teacherId} onChange={e=>setTeacherId(e.target.value)}>
            <option value="">— Select teacher (optional) —</option>
            {teachers.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
          </select>

          {teacherId && (
            <TeacherSlotPicker teacherId={teacherId} durationMinutes={duration} selectedDate={date}
              onPick={({ date: d, time: t }) => { setDate(d); if (t) { setTime(t); setTimeInput(fmtTime(t)) } }}/>
          )}

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
            <div>
              <label style={lbl}>Date *</label>
              <input style={inp} type="date" value={date} onChange={e=>setDate(e.target.value)} min={new Date().toISOString().slice(0,10)} required />
            </div>
            <div>
              <label style={lbl}>Time (IST)</label>
              <input style={{ ...inp, cursor:'text', opacity:1, WebkitTextFillColor:'rgba(var(--fg-rgb),var(--a85))' }} type="text" inputMode="numeric" value={timeInput} placeholder="07:30 PM" onChange={e=>setTimeInput(e.target.value)} required />
            </div>
          </div>

          {availWarning && (
            <div style={{ marginTop:'10px', padding:'10px 12px', background:'rgba(244,163,53,0.1)', border:'0.5px solid rgba(244,163,53,0.3)', borderRadius:'9px', fontSize:'12px', color:'#f4a335' }}>
              ⚠ {availWarning}. <button type="button" disabled={busy} onClick={() => submit(null, true)}
                style={{ background:'none', border:'none', color:'var(--fg-solid)', fontWeight:800, textDecoration:'underline', cursor:busy?'not-allowed':'pointer', fontFamily:'inherit', fontSize:'12px', padding:0 }}>
                Schedule anyway →
              </button>
            </div>
          )}

          <label style={lbl}>Duration</label>
          <select style={{ ...inp, colorScheme:'dark' }} value={duration} onChange={e=>setDuration(Number(e.target.value))}>
            <option value={30}>30 minutes</option>
            <option value={45}>45 minutes</option>
            <option value={60}>1 hour</option>
            <option value={90}>1.5 hours</option>
          </select>

          <label style={lbl}>Notes (optional)</label>
          <textarea style={{ ...inp, minHeight:'60px', resize:'vertical' }} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Any notes about the lead or demo..." />

          {lead.email && (
            <div style={{ display:'flex', alignItems:'center', gap:'10px', marginTop:'10px', padding:'9px 12px', background:'rgba(var(--fg-rgb),0.04)', borderRadius:'9px', cursor:'pointer' }} onClick={()=>setSendMail(!sendMail)}>
              <div style={{ width:'34px', height:'18px', borderRadius:'9px', background:sendMail?'#8b5cf6':'rgba(var(--fg-rgb),0.1)', position:'relative', flexShrink:0, transition:'background 0.2s' }}>
                <div style={{ position:'absolute', top:'2px', left:sendMail?'18px':'2px', width:'14px', height:'14px', borderRadius:'50%', background:'#fff', transition:'left 0.2s' }}/>
              </div>
              <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a60))' }}>
                Send demo confirmation email to {showContact
                  ? <strong style={{ color:'rgba(var(--fg-rgb),var(--a80))' }}>{lead.email}</strong>
                  : 'the lead'}
              </span>
            </div>
          )}

          {!lead.email && (
            <div style={{ marginTop:'10px', padding:'9px 12px', background:'rgba(244,163,53,0.08)', border:'0.5px solid rgba(244,163,53,0.2)', borderRadius:'9px', fontSize:'12px', color:'#f4a335' }}>
              ⚠ No email on file — confirmation email will not be sent
            </div>
          )}

          {err && <div style={{ marginTop:'8px', padding:'8px 12px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'8px', fontSize:'12px' }}>{err}</div>}

          <div style={{ display:'flex', gap:'8px', marginTop:'16px' }}>
            <button type="submit" disabled={busy} style={{ flex:1, padding:'12px', background:busy?'rgba(100,100,100,0.3)':'linear-gradient(135deg,#8b5cf6,#6d28d9)', color:'#fff', fontSize:'14px', fontWeight:800, border:'none', borderRadius:'10px', cursor:busy?'not-allowed':'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
              {busy
                ? <><span style={{ display:'inline-block', width:'14px', height:'14px', border:'2px solid rgba(var(--fg-rgb),var(--a30))', borderTopColor:'#fff', borderRadius:'50%', animation:'spin .7s linear infinite' }}/>Scheduling...</>
                : '🎯 Schedule Demo'}
            </button>
            <button type="button" onClick={onClose} style={{ padding:'12px 18px', background:'rgba(var(--fg-rgb),0.06)', color:'rgba(var(--fg-rgb),var(--a50))', border:'none', borderRadius:'10px', cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
          </div>
        </form>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  )
}

export default function ScheduleDemo({ profile, showContact = true }) {
  // Sales see students as information only (Student ID, teacher, course),
  // with no contact details and no booking button.
  const isSales = profile?.role === 'sales'
  const [studentInfo, setStudentInfo] = useState({})
  const [leads,     setLeads]     = useState([])
  const [students,  setStudents]  = useState([])
  const [teachers,  setTeachers]  = useState([])
  const [upcomingDemos, setUpcoming] = useState([])
  const [loading,   setLoading]   = useState(true)
  const [tab,       setTab]       = useState('leads')
  const [modal,     setModal]     = useState(null)
  const [ok,        setOk]        = useState('')
  const [search,    setSearch]    = useState('')

  useEffect(() => { loadAll() }, [])

  async function loadAll() {
    setLoading(true)
    const today = new Date().toISOString().slice(0,10)
    const [{ data: l }, { data: s }, { data: t }, { data: d }] = await Promise.all([
      supabase.from('leads').select('*').not('status','eq','Enrolled').order('created_at', { ascending:false }),
      supabase.from('profiles').select('id,full_name,email,timezone,student_id').eq('role','student').order('full_name'),
      supabase.from('profiles').select('id,full_name').eq('role','teacher').order('full_name'),
      supabase.from('classes').select('*').eq('batch','demo').gte('class_date', today).order('class_date'),
    ])
    setLeads(l || [])
    setStudents(s || [])
    setTeachers(t || [])
    setUpcoming(d || [])
    setLoading(false)

    // Each student's teachers and courses, from their enrollments.
    const [{ data: enr }, { data: courses }] = await Promise.all([
      supabase.from('enrollments').select('student_id, course_id, classes(teacher_name, batch)'),
      supabase.from('courses').select('id,title'),
    ])
    const courseTitle = Object.fromEntries((courses || []).map(c => [c.id, c.title]))
    const info = {}
    for (const e of enr || []) {
      const row = info[e.student_id] || (info[e.student_id] = { teachers: new Set(), courses: new Set() })
      if (e.classes?.teacher_name) row.teachers.add(e.classes.teacher_name)
      const course = courseTitle[e.course_id] || (e.classes?.batch !== 'demo' ? e.classes?.batch : null)
      if (course) row.courses.add(course)
    }
    setStudentInfo(info)
  }

  const q = search.toLowerCase()
  const filteredLeads    = leads.filter(l => !search || l.full_name?.toLowerCase().includes(q) || (showContact && l.phone?.includes(search)))
  const filteredStudents = students.filter(s => !search || s.full_name?.toLowerCase().includes(q) || s.student_id?.toLowerCase().includes(q) || (showContact && s.email?.toLowerCase().includes(q)))

  const inp = { background:'var(--bg-input)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', padding:'9px 12px', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', outline:'none', fontFamily:'inherit', colorScheme:'dark' }

  return (
    <div style={{ marginTop:'14px' }}>
      <div style={{ fontSize:'16px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a85))', marginBottom:'4px' }}>🎯 Schedule Demo</div>
      <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a30))', marginBottom:'16px' }}>
        Book a free demo class — confirmation email sent automatically
      </div>

      {ok && <div style={{ padding:'9px 14px', background:'rgba(16,185,129,0.1)', border:'0.5px solid rgba(16,185,129,0.25)', borderRadius:'9px', fontSize:'13px', color:'#34d399', marginBottom:'12px' }}>{ok}</div>}

      <div style={{ display:'flex', gap:'6px', marginBottom:'16px', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.07)', paddingBottom:'12px' }}>
        {[
          { id:'leads',    label:`📋 Leads (${leads.length})`                    },
          { id:'students', label:`🎓 Students (${students.length})`              },
          { id:'upcoming', label:`📅 Upcoming Demos (${upcomingDemos.length})`   },
        ].map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)} style={{ fontSize:'12px', fontWeight:700, padding:'7px 16px', borderRadius:'10px', border:'none', cursor:'pointer', fontFamily:'inherit', background:tab===t.id?'linear-gradient(135deg,#8b5cf6,#6d28d9)':'rgba(var(--fg-rgb),0.05)', color:tab===t.id?'#fff':'rgba(var(--fg-rgb),var(--a45))' }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab !== 'upcoming' && (
        <input style={{ ...inp, width:'100%', marginBottom:'12px', boxSizing:'border-box' }} type="text"
          placeholder={tab==='leads' ? '🔍 Search leads by name or phone...' : '🔍 Search students...'}
          value={search} onChange={e=>setSearch(e.target.value)} />
      )}

      {loading ? (
        <div style={{ textAlign:'center', padding:'3rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>Loading...</div>
      ) : (
        <>
          {tab === 'leads' && (
            <div style={{ display:'grid', gap:'8px' }}>
              {filteredLeads.length === 0
                ? <div style={{ textAlign:'center', padding:'3rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>No leads found</div>
                : filteredLeads.map(lead => {
                  const ss = STATUS_STYLE[lead.status] || STATUS_STYLE['New']
                  return (
                    <div key={lead.id} style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:'10px 12px', padding:'12px 16px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'12px', border:'0.5px solid rgba(var(--fg-rgb),0.07)' }}>
                      <div style={{ width:'36px', height:'36px', borderRadius:'50%', flexShrink:0, background:'rgba(139,92,246,0.18)', color:'#a78bfa', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px', fontWeight:800 }}>
                        {lead.full_name?.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex:'1 1 200px', minWidth:0 }}>
                        <div style={{ fontSize:'13px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a85))' }}>{lead.full_name}</div>
                        <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a35))', marginTop:'2px' }}>
                          {showContact && lead.phone && `📞 ${lead.phone} · `}
                          {showContact && lead.email && `${lead.email} · `}
                          {lead.course_interest && `🎸 ${lead.course_interest}`}
                        </div>
                        {lead.demo_date && (
                          <div style={{ fontSize:'10px', color:'#f4a335', marginTop:'2px' }}>
                            📅 Demo: {fmtDate(lead.demo_date?.slice(0,10))} {lead.demo_date?.slice(11,16) && fmtTime(lead.demo_date.slice(11,16))}
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize:'10px', fontWeight:700, padding:'3px 9px', borderRadius:'8px', background:ss.bg, color:ss.color, flexShrink:0 }}>
                        {lead.status || 'New'}
                      </span>
                      <button onClick={() => setModal(lead)} style={{ fontSize:'12px', fontWeight:700, padding:'7px 16px', borderRadius:'9px', background:'rgba(139,92,246,0.15)', color:'#a78bfa', border:'0.5px solid rgba(139,92,246,0.25)', cursor:'pointer', fontFamily:'inherit', flexShrink:0, whiteSpace:'nowrap' }}>
                        🎯 {lead.demo_date ? 'Reschedule' : 'Schedule Demo'}
                      </button>
                    </div>
                  )
                })
              }
            </div>
          )}

          {tab === 'students' && (
            <div style={{ display:'grid', gap:'8px' }}>
              {filteredStudents.length === 0
                ? <div style={{ textAlign:'center', padding:'3rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>No students found</div>
                : filteredStudents.map(stu => (
                  <div key={stu.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 16px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'12px', border:'0.5px solid rgba(var(--fg-rgb),0.07)' }}>
                    <div style={{ width:'36px', height:'36px', borderRadius:'50%', flexShrink:0, background:'rgba(30,144,255,0.18)', color:'#5aabff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px', fontWeight:800 }}>
                      {stu.full_name?.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
                        <span style={{ fontSize:'13px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a85))' }}>{stu.full_name}</span>
                        {stu.student_id && <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 8px', borderRadius:'6px', background:'rgba(232,124,30,0.15)', color:'#f4a335', letterSpacing:'0.04em' }}>ID {stu.student_id}</span>}
                      </div>
                      {isSales ? (
                        <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a45))', marginTop:'3px' }}>
                          👨‍🏫 {[...(studentInfo[stu.id]?.teachers || [])].join(', ') || 'No teacher yet'}
                          {' · '}🎸 {[...(studentInfo[stu.id]?.courses || [])].join(', ') || 'No course yet'}
                        </div>
                      ) : (
                        <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a35))', marginTop:'2px' }}>{stu.email}</div>
                      )}
                    </div>
                    {!isSales && <button onClick={() => setModal({ ...stu, phone:'', course_interest:'', status:'Student', isStudent:true })} style={{ fontSize:'12px', fontWeight:700, padding:'7px 16px', borderRadius:'9px', background:'rgba(30,144,255,0.12)', color:'#5aabff', border:'0.5px solid rgba(30,144,255,0.2)', cursor:'pointer', fontFamily:'inherit', flexShrink:0 }}>
                      🎯 Schedule Demo
                    </button>}
                  </div>
                ))
              }
            </div>
          )}

          {tab === 'upcoming' && (
            <div style={{ display:'grid', gap:'8px' }}>
              {upcomingDemos.length === 0
                ? <div style={{ textAlign:'center', padding:'3rem 1rem', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'14px', border:'0.5px solid rgba(var(--fg-rgb),0.07)', color:'rgba(var(--fg-rgb),var(--a25))', fontSize:'13px' }}>
                    No upcoming demos scheduled yet
                  </div>
                : upcomingDemos.map(demo => (
                  <div key={demo.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 16px', background:'rgba(139,92,246,0.06)', borderRadius:'12px', border:'0.5px solid rgba(139,92,246,0.18)' }}>
                    <div style={{ fontSize:'24px', flexShrink:0 }}>🎯</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'13px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a85))' }}>{demo.title}</div>
                      <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a35))', marginTop:'2px' }}>
                        📅 {fmtDate(demo.class_date)}
                        {demo.start_time && ` · ⏰ ${fmtTime(demo.start_time)}`}
                        {demo.teacher_name && ` · 👨‍🏫 ${demo.teacher_name}`}
                      </div>
                    </div>
                    {demo.meet_link ? (
                      <a href={demo.meet_link} target="_blank" rel="noreferrer" style={{ fontSize:'11px', fontWeight:700, padding:'5px 12px', borderRadius:'8px', background:'rgba(30,144,255,0.15)', color:'#5aabff', border:'0.5px solid rgba(30,144,255,0.25)', textDecoration:'none', flexShrink:0, whiteSpace:'nowrap' }}>
                        🎥 Join
                      </a>
                    ) : (
                      <span style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a20))', flexShrink:0 }}>No link yet</span>
                    )}
                    {(() => {
                      const daysLeft = Math.ceil((new Date(demo.class_date) - new Date()) / 86400000)
                      return (
                        <span style={{ fontSize:'10px', fontWeight:700, padding:'3px 9px', borderRadius:'8px', background: daysLeft <= 1 ? 'rgba(239,68,68,0.15)' : daysLeft <= 3 ? 'rgba(244,163,53,0.15)' : 'rgba(139,92,246,0.15)', color: daysLeft <= 1 ? '#f87171' : daysLeft <= 3 ? '#f4a335' : '#a78bfa', flexShrink:0 }}>
                          {daysLeft === 0 ? 'Today!' : daysLeft === 1 ? 'Tomorrow' : `In ${daysLeft} days`}
                        </span>
                      )
                    })()}
                  </div>
                ))
              }
            </div>
          )}
        </>
      )}

      {modal && (
        <ScheduleDemoModal
          lead={modal}
          creditTo={isSales ? profile.id : null}
          showContact={showContact}
          onClose={() => setModal(null)}
          onSaved={(meetLink) => {
            setModal(null)
            setOk(`✓ Demo scheduled for ${modal.full_name}! ${meetLink ? '🎥 Meeting link generated.' : ''} ${modal.email ? '📧 Confirmation email sent.' : ''}`)
            setTimeout(() => setOk(''), 6000)
            loadAll()
          }}
        />
      )}
    </div>
  )
}
